<template>
  <div>
    <div>
      <div class="font-weight-bold mb-1 mt-5">
        Diversity
      </div>
      <v-skeleton-loader
        type="list-item-three-line"
        v-if="isFetchingUserDiversityTraits"
      />
      <div v-for="(step, index) in allSteps" :key="index" v-else>
        <component
          :userData="self"
          :userDiversityTraits="userDiversityTraits"
          :is="step"
          v-if="
            shouldStepViewBeShown(index) || ifStepHasAlreadyBeenCompleted(index)
          "
          v-show="shouldStepViewBeShown(index)"
          :isLoading="isEditingUserDetails"
          @nextStep="stepForward"
          :isEditMode="isEditMode"
          submitTitle="Continue"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Gender from "@/views/profile/QuestionsAndAnswers/steps/Diversity/steps/Gender";
import Cisgendered from "@/views/profile/QuestionsAndAnswers/steps/Diversity/steps/Cisgendered";
import Age from "@/views/profile/QuestionsAndAnswers/steps/Diversity/steps/Age";
import SexualOrientation from "@/views/profile/QuestionsAndAnswers/steps/Diversity/steps/SexualOrientation";
import Religion from "@/views/profile/QuestionsAndAnswers/steps/Diversity/steps/Religion";
import MaritalStatus from "@/views/profile/QuestionsAndAnswers/steps/Diversity/steps/MaritalStatus";
import CareResponsibilities from "@/views/profile/QuestionsAndAnswers/steps/Diversity/steps/CareResponsibilities";
import Disability from "@/views/profile/QuestionsAndAnswers/steps/Diversity/steps/Disability";
import Ethnicity from "@/views/profile/QuestionsAndAnswers/steps/Diversity/steps/Ethnicity";
import { createNamespacedHelpers } from "vuex";
import { copyState } from "@/utils/helpers";
import {
  EDIT_USER,
  ADD_USER_DIVERSITY_TRAITS
} from "@/store/modules/users/actions";
import { AUTH_NAMESPACE } from "@/store/modules/auth";
import { USERS_NAMESPACE } from "@/store/modules/users";
import { size } from "lodash";
import { FETCH_USER_DIVERSITY_TRAITS } from "@/store/modules/users/actions";
import { USER_STATUSES } from "@/constants/users";
import { isPermissioned } from "@/utils/permissions";
import { PERMISSIONS } from "@/constants/workers";

const { DIVERSITY: DIVERSITY_PERMISSIONS } = PERMISSIONS;

const { mapState: mapAuthState } = createNamespacedHelpers(AUTH_NAMESPACE);
const { mapState: mapUserState, mapActions } = createNamespacedHelpers(
  USERS_NAMESPACE
);
export default {
  props: {
    isEditMode: Boolean
  },
  created() {
    this.fetchUserDiversityTraits(this.self.id);
    this.emitProgressValue({ step: this.steps.current });
  },
  data() {
    return {
      userData: {
        divirsityTraits: null,
        mainDetails: null
      },
      steps: {
        current: 1,
        saved: 0
      },
      showValidationErrors: false,
      allSteps: [
        this.showStep(DIVERSITY_PERMISSIONS.gender) && Gender,
        this.showStep(DIVERSITY_PERMISSIONS.cisgendered) && Cisgendered,
        this.showStep(DIVERSITY_PERMISSIONS.age) && Age,
        this.showStep(DIVERSITY_PERMISSIONS.sexualOrientation) &&
          SexualOrientation,
        this.showStep(DIVERSITY_PERMISSIONS.ethnicity) && Ethnicity,
        this.showStep(DIVERSITY_PERMISSIONS.religion) && Religion,
        this.showStep(DIVERSITY_PERMISSIONS.maritalStatus) && MaritalStatus,
        this.showStep(DIVERSITY_PERMISSIONS.careResponsibilities) &&
          CareResponsibilities,
        this.showStep(DIVERSITY_PERMISSIONS.disabilities) && Disability
      ].filter(Boolean)
    };
  },
  computed: {
    ...mapAuthState({
      self: state => state.userData
    }),
    ...mapUserState(
      copyState([
        "isEditingUserDetails",
        "isFetchingUserDiversityTraits",
        "userDiversityTraits"
      ])
    ),
    totalSteps() {
      return size(this.allSteps);
    },
    progressValue() {
      return (this.steps.current / this.totalSteps) * 100;
    },
    // needed value for parent form component
    showBackButton() {
      return this.steps.current > 1;
    },
    shouldUpdateUserStatus() {
      return this.self.status === USER_STATUSES.INCOMPLETE;
    }
  },
  methods: {
    ...mapActions({
      editUser: EDIT_USER,
      addUserDiversityTraits: ADD_USER_DIVERSITY_TRAITS,
      fetchUserDiversityTraits: FETCH_USER_DIVERSITY_TRAITS
    }),
    async emitProgressValue({ step }) {
      const progressValue = (step / this.totalSteps) * 100;
      if (progressValue > 100) {
        await this.updateUserData();
        this.onClose();
      }
      this.$emit("onStepChanged", {
        progressValue,
        step,
        totalSteps: this.totalSteps
      });
    },
    async updateUserData() {
      const { mainDetails, divirsityTraits } = this.userData;
      if (mainDetails || !this.isEditMode) {
        await this.editUser({
          id: this.self.id,
          data: {
            ...(mainDetails && mainDetails),
            ...(this.shouldUpdateUserStatus && {
              status: USER_STATUSES.ACTIVE
            })
          }
        });
      }
      if (divirsityTraits) {
        await this.addUserDiversityTraits({
          id: this.self.id,
          data: divirsityTraits
        });
      }
    },
    onClose() {
      this.$emit("onFinishEditingProfile");
    },
    async stepForward({ data, dataType }) {
      if (data) {
        this.userData[dataType] = { ...this.userData[dataType], ...data };
      }
      if (
        this.steps.saved < this.totalSteps &&
        this.steps.current > this.steps.saved
      ) {
        this.steps.saved += 1;
      }
      this.emitProgressValue({ step: this.steps.current + 1 });
      if (this.steps.current < this.totalSteps) this.steps.current += 1;
    },
    stepBack() {
      this.emitProgressValue({ step: this.steps.current - 1 });
      if (this.steps.current > 1) {
        this.steps.current--;
      }
    },
    shouldStepViewBeShown(index) {
      const viewStep = index + 1;
      return viewStep <= this.totalSteps && this.steps.current === viewStep;
    },
    ifStepHasAlreadyBeenCompleted(index) {
      const viewStep = index + 1;
      return viewStep <= this.steps.saved;
    },
    showStep(permission) {
      return isPermissioned(`workers.edit.${permission}`);
    }
  }
};
</script>

<style lang="scss"></style>
